import React, { useState, useRef } from 'react';

import { useParams } from 'react-router-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import capitalize from 'lodash/capitalize';

import Invoice from './Invoice';
import PaymentForm from './PaymentForm';
import Alert from '../../../../Alert';
import ReversePaymentForm from './ReversePaymentForm';
import AchCheckStatus from '../../../../AchCheckStatus';
import DemandAlert from '../../DemandAlert';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';

import useInvoiceQueries from './helpers/useInvoiceQueries';
import getFundsQueryOptions from './helpers/getFundsQueryOptions';
import PaymentService from '../../../../../services/PaymentService';
import { useUiContext } from '../../../../../context/UiContext';
import { createAchCheckLabels } from './helpers';
import { useServicerStore } from '../../../../../store';
import LoanService from '../../../../../services/LoanService';
import ActionsMenu from '../../../../ActionsMenu';
import usePaymentActions from './hooks/usePaymentActions';

const tabs = [
  {
    label: 'Payments',
    activeOn: 'Payment',
  },
  {
    label: 'Demands',
  },
];

export default function Payment({ refetchLoan, loan }) {
  const [fundsCurrentOption, setFundsCurrentOption] = useState(
    'apply_to_next_invoice'
  );
  const [skipLineItemsIds, setSkipLineItemsIds] = useState([]);

  const { showToast, showDialogModal } = useUiContext();

  const { paymentId } = useParams();

  const previousInvoices = useRef([]);
  const lastInvoiceTableRef = useRef();

  const queryClient = useQueryClient();

  const userRole = useServicerStore((state) => state.userRole);
  const isLender = userRole === 'lender';
  const isBorrower = userRole === 'borrower';
  const readOnly = isLender || isBorrower;

  const {
    data: payment,
    refetch,
    isFetching: isPaymentFetching,
  } = useQuery({
    queryKey: ['payment', paymentId],
    queryFn: () => PaymentService.getPayment(paymentId),
    enabled: !!paymentId,
    keepPreviousData: true,
  });

  const isPaymentApplied = payment?.status === 'applied';
  const isPaymentPartiallyApplied = payment?.status === 'partial';
  const isPaymentVoid =
    payment?.status === 'void' || payment?.status === 'cancelled';
  const isPaymentUnapplied = payment?.status === 'unapplied';
  const isPaymentPaid = isPaymentApplied || isPaymentPartiallyApplied;

  const queryOptions = getFundsQueryOptions(skipLineItemsIds);

  const {
    data: paymentPlan,
    isFetching: isPaymetnPlanFetching,
    refetch: refetchPaymentPlan,
  } = useQuery({
    queryKey: ['paymentPlan', paymentId, fundsCurrentOption, skipLineItemsIds],
    queryFn: () => {
      return PaymentService.getPaymentPlan({
        paymentId: payment.id,
        ...queryOptions,
      });
    },
    enabled: !!payment && !isPaymentApplied && !isPaymentVoid,
    keepPreviousData: true,
  });

  const { data: demands } = useQuery({
    queryKey: ['demands', loan?.id],
    queryFn: () => LoanService.getLoanDemands(loan?.id),
    enabled: !!loan?.id,
  });

  const activeDemands = demands?.filter((demand) => demand.status === 'active');
  const hasActiveDemands = activeDemands?.length > 0;

  const { mutate: distributePayment, isPending } = useMutation({
    mutationFn: () => {
      return PaymentService.distributePayment({
        paymentId: payment.id,
        ...queryOptions,
      });
    },
    onSuccess: () => {
      refetch();
      refetchLoan();
      queryClient.invalidateQueries({ queryKey: ['demands', loan?.id] });
      showToast({
        text: 'Payment distributed',
        type: 'success',
      });
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const distributePaymentWithConfirmation = () => {
    showDialogModal({
      title: 'Apply payment',
      message:
        'There is an active demand on this loan. Applying a payment will satisfy the demand and delay and necessitate a new demand for any unpaid invoices. Are you sure you want to apply this payment?',
      onConfirm: () => distributePayment(),
      confirmButtonLabel: 'Yes, apply the payment',
      cancelButtonLabel: 'No, do NOT apply the payment',
    });
  };

  const { mutate: updatePayment } = useMutation({
    mutationFn: (formValues) => {
      return PaymentService.updatePayment({
        paymentId: payment.id,
        formValues: {
          ...formValues,
          bank_account_id:
            payment?.source === 'ACH'
              ? formValues.bank_account_id || payment?._loan?.bank_account_id
              : null,
        },
      });
    },
    onSuccess: () => {
      refetch().then(() => {
        console.log('refetching payment plan');
        refetchPaymentPlan();
      });
      refetchLoan();
      showToast({
        text: 'Payment updated',
        type: 'success',
      });
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const invoices = useInvoiceQueries(paymentPlan?.distribution_plan);

  // useQueries hook does not support keepPreviousData option
  // so we need to store previous data manually to avoid tables flickering
  if (invoices?.length > 0) {
    previousInvoices.current = { data: invoices, fundsCurrentOption };
  }

  let invoicesSource =
    invoices?.length ||
    previousInvoices.current?.fundsCurrentOption !== fundsCurrentOption
      ? invoices
      : previousInvoices.current?.data;

  // If payment is applied, instead of invoices from payment plan, we need to show invoices from payment itself
  if (isPaymentApplied) {
    invoicesSource = payment?._invoices ?? [];
  }

  // If payment is partially applied, we need to show first invoices from the payment itself and then from the payment plan
  if (isPaymentPartiallyApplied) {
    invoicesSource = [...(payment?._invoices ?? []), ...invoicesSource];
    // Remove duplicates by id
    const uniqueInvoices = {};
    invoicesSource?.forEach((invoice) => {
      if (!invoice || !invoice.id) {
        return;
      }
      uniqueInvoices[invoice.id] = invoice;
    });

    invoicesSource = Object.values(uniqueInvoices);
  }

  const hasPTypeInvoice = invoicesSource?.some(
    (invoice) => invoice?.type === 'P'
  );

  const hasNoDistributionPlan = !paymentPlan?.distribution_plan?.length;
  const hasNoScheduledInvoices =
    queryOptions.includeScheduled &&
    !invoicesSource?.some((invoice) => invoice?.status === 'scheduled');
  const willBeAppliedToPrincipal =
    fundsCurrentOption === 'apply_to_principal' && hasNoDistributionPlan;

  const isFetching = isPaymentFetching || isPaymetnPlanFetching;

  // Sort invoices the same way as in the distribution plan
  if (!hasNoDistributionPlan) {
    const order = paymentPlan?.distribution_plan?.map(
      (item) => item.invoice_id
    );

    invoicesSource = invoicesSource.slice().sort((a, b) => {
      const i = order.indexOf(a.id);
      const j = order.indexOf(b.id);
      // Put unmatched ones at the end
      const ia = i === -1 ? Infinity : i;
      const jb = j === -1 ? Infinity : j;
      return ia - jb;
    });
  }

  const isFinished =
    payment?.status === 'applied' || payment?.status === 'paid';

  const { actions, isDeletingPayment } = usePaymentActions({
    payment,
    refetch,
    readOnly,
  });

  return (
    <div>
      <div className="mb-4">
        <AddOrEditPageTabs tabs={tabs} />
      </div>
      <div className="relative p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        <div className="absolute right-7 flex flex-col items-end md:flex-row md:items-stretch gap-3">
          <AchCheckStatus labels={createAchCheckLabels(payment)} />
          <div className="h-8 flex">
            <ActionsMenu
              actions={actions}
              badgeText={capitalize(payment?.status)}
              badgeColor={isFinished ? 'emerald' : 'amber'}
            />
          </div>
        </div>
        <PaymentForm
          readOnly={readOnly}
          loan={loan}
          isPending={isPending || isDeletingPayment}
          isFetching={isFetching}
          payment={payment}
          isPaymentApplied={isPaymentApplied}
          fundsCurrentOption={fundsCurrentOption}
          paymentPlan={paymentPlan}
          setFundsCurrentOption={setFundsCurrentOption}
          distributePayment={
            hasActiveDemands
              ? distributePaymentWithConfirmation
              : distributePayment
          }
          updatePayment={updatePayment}
          disabled={
            isPaymentApplied ||
            isPaymentVoid ||
            (hasNoDistributionPlan && fundsCurrentOption === 'do_not_apply')
          }
          isPaymentVoid={isPaymentVoid}
          isPaymentUnapplied={isPaymentUnapplied}
          source={payment?.source}
          hasPTypeInvoice={hasPTypeInvoice}
          isPaymentPaid={isPaymentPaid}
        />
        {!isPaymentApplied && (
          <DemandAlert loanId={loan?.id} className="mt-5" />
        )}
        {!invoicesSource?.length &&
        !isFetching &&
        !isPaymentApplied &&
        !isPaymentVoid &&
        !readOnly ? (
          <p
            className="text-center text-orange-400 dark:text-orange-300
      text-lg mt-10 mb-10"
          >
            No invoices meet this criteria.
          </p>
        ) : null}
        {invoicesSource?.map((invoice) => {
          return (
            <Invoice
              key={invoice?.id}
              invoice={invoice}
              distributionPlan={paymentPlan?.distribution_plan}
              skipLineItemsIds={skipLineItemsIds}
              setSkipLineItemsIds={setSkipLineItemsIds}
              isPaymentApplied={isPaymentApplied}
              isPaymentPartiallyApplied={isPaymentPartiallyApplied}
              payment={payment}
              isPaymentPaid={isPaymentPaid}
              readOnly={readOnly}
              isBorrower={isBorrower}
              loanId={loan?.id}
            />
          );
        })}
        {(isPaymentUnapplied || isPaymentPartiallyApplied) &&
          invoicesSource?.length > 1 && (
            <Invoice
              invoicesSource={invoicesSource}
              invoice={invoicesSource?.at(-1)}
              distributionPlan={paymentPlan?.distribution_plan}
              skipLineItemsIds={skipLineItemsIds}
              setSkipLineItemsIds={setSkipLineItemsIds}
              isPaymentApplied={isPaymentApplied}
              isPaymentPartiallyApplied={isPaymentPartiallyApplied}
              payment={payment}
              isPaymentPaid={isPaymentPaid}
              summary
              lastInvoiceTableRef={lastInvoiceTableRef}
              isBorrower={isBorrower}
            />
          )}
        {hasNoScheduledInvoices && !isPaymentVoid && !readOnly && (
          <Alert message="There are currently no scheduled invoices" />
        )}
        {willBeAppliedToPrincipal && (
          <Alert message="This payment will not be applied to invoices, but will be applied to principal" />
        )}
        {!isPaymentVoid && !isPaymentUnapplied && !readOnly && (
          <div className="mt-10">
            <ReversePaymentForm
              lastInvoiceTableRef={lastInvoiceTableRef}
              refetch={refetch}
              paymentId={paymentId}
              isDistributed={payment?.distributed}
            />
          </div>
        )}
      </div>
    </div>
  );
}
